/**
 * Response body readers that tolerate non-JSON (HTML/empty) payloads.
 *
 * collins-api renders HTML error pages when `Accept: application/json` is not
 * negotiated, so calling response.json() on an error body surfaces a JSON
 * parse error instead of the real HTTP failure. These helpers read the body
 * once, as text, and only JSON.parse bodies that are actually JSON.
 */

const OBJECT_ID_SEGMENT = /^[a-f0-9]{24}$/i;
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIGITS_SEGMENT = /^\d+$/;

const isIdSegment = (segment: string): boolean =>
  OBJECT_ID_SEGMENT.test(segment) ||
  UUID_SEGMENT.test(segment) ||
  DIGITS_SEGMENT.test(segment);

/**
 * Normalise a request URL to a stable endpoint path for telemetry grouping:
 * the query string is dropped and id-like path segments (Mongo ObjectIds,
 * UUIDs, numeric ids) are collapsed to `:id`. Mock Responses have no `url`,
 * and any unparseable value, returns "unknown".
 */
export const getEndpointPath = (url: string | undefined): string => {
  if (!url) {
    return "unknown";
  }

  try {
    const { pathname } = new URL(url);
    return pathname
      .split("/")
      .map((segment) => (isIdSegment(segment) ? ":id" : segment))
      .join("/");
  } catch {
    return "unknown";
  }
};

const getContentType = (response: Response): string =>
  response.headers?.get?.("content-type") ?? "";

const looksLikeJson = (text: string): boolean =>
  text.startsWith("{") || text.startsWith("[");

const parseJsonText = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

/**
 * Read an error response body without ever throwing.
 *
 * Returns the parsed JSON body when the payload is JSON, undefined for HTML,
 * empty, or unparseable bodies. Response-like test doubles without a `text`
 * function fall back to `.json()`.
 */
export const readErrorBody = async (response: Response): Promise<unknown> => {
  try {
    if (typeof response?.text !== "function") {
      return await response?.json?.();
    }

    const text = await response.text();
    const trimmed = text.trim();
    if (!trimmed) {
      return undefined;
    }

    const contentType = getContentType(response);
    if (contentType.includes("json")) {
      return parseJsonText(trimmed);
    }

    if (
      (contentType === "" || contentType.includes("text/plain")) &&
      looksLikeJson(trimmed)
    ) {
      return parseJsonText(trimmed);
    }

    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Read a success response body as JSON.
 *
 * Returns undefined for 204 and empty bodies so callers can fall back to an
 * empty result. Invalid JSON on a 2xx propagates — the caller's catch routes
 * it through errorHandler like any other failure. Response-like test doubles
 * without a `text` function fall back to `.json()`.
 */
export const readJsonBody = async <T>(
  response: Response
): Promise<T | undefined> => {
  if (response.status === 204) {
    return undefined;
  }

  if (typeof response?.text !== "function") {
    return (await response.json()) as T;
  }

  const text = await response.text();
  if (!text.trim()) {
    return undefined;
  }

  return JSON.parse(text) as T;
};
